

import ISiteObjectDto from "./SiteObjectDto.if";
import mappings from "./SiteObjectDto.map";

import Transformers from './private/transformer';
import DBUtil from './private/db.util';
import { IModelSource } from "./private/db.if";

import ISiteDto from "./SiteDto.if";
import SiteDto from "./SiteDto";
import ISiteObjectTypeDto from "./SiteObjectTypeDto.if";
import SiteObjectTypeDto from "./SiteObjectTypeDto";
import IStyleDto from "./StyleDto.if";
import StyleDto from "./StyleDto";
import IMediumDto from "./MediumDto.if";
import MediumDto from "./MediumDto";
import IBeaconSensitivityDto from "./BeaconSensitivityDto.if";
import BeaconSensitivityDto from "./BeaconSensitivityDto";
import IBeaconDto from "./BeaconDto.if";
import BeaconDto from "./BeaconDto";
import ISiteObjectTranslationDto from "./SiteObjectTranslationDto.if";
import SiteObjectTranslationDto from "./SiteObjectTranslationDto";
import ILanguageDto from "./LanguageDto.if";


function _site(this: ISiteObjectDto, db: IModelSource): Promise<ISiteDto> {
    return DBUtil.loadSiteId<ISiteDto>(db, this.siteId, SiteDto);
}

function _type(this: ISiteObjectDto, db: IModelSource): Promise<ISiteObjectTypeDto> {
    return DBUtil.loadSiteObjectTypeId<ISiteObjectTypeDto>(db, this.siteObjectTypeId, SiteObjectTypeDto);
}

function _beacon(this: ISiteObjectDto, db: IModelSource): Promise<IBeaconDto> {
    return DBUtil.loadBeaconId<IBeaconDto>(db, this.beaconId, BeaconDto);
}

function _beaconSensitivity(this: ISiteObjectDto, db: IModelSource): Promise<IBeaconSensitivityDto> {
    return DBUtil.loadBeaconSensitivityId<IBeaconSensitivityDto>(db, this.beaconSensitivityId, BeaconSensitivityDto);
}

function _nextObject(this: ISiteObjectDto, db: IModelSource): Promise<ISiteObjectDto> {
    return DBUtil.loadSiteObjectId<ISiteObjectDto>(db, this.nextObjectId, SiteObjectDto);
}

function _relatedObject1(this: ISiteObjectDto, db: IModelSource): Promise<ISiteObjectDto> {
    return DBUtil.loadSiteObjectId<ISiteObjectDto>(db, this.relatedObject1Id, SiteObjectDto);
}

function _relatedObject2(this: ISiteObjectDto, db: IModelSource): Promise<ISiteObjectDto> {
    return DBUtil.loadSiteObjectId<ISiteObjectDto>(db, this.relatedObject2Id, SiteObjectDto);
}

function _relatedObject3(this: ISiteObjectDto, db: IModelSource): Promise<ISiteObjectDto> {
    return DBUtil.loadSiteObjectId<ISiteObjectDto>(db, this.relatedObject3Id, SiteObjectDto);
}

function _relatedObject4(this: ISiteObjectDto, db: IModelSource): Promise<ISiteObjectDto> {
    return DBUtil.loadSiteObjectId<ISiteObjectDto>(db, this.relatedObject4Id, SiteObjectDto);
}

function _relatedObject5(this: ISiteObjectDto, db: IModelSource): Promise<ISiteObjectDto> {
    return DBUtil.loadSiteObjectId<ISiteObjectDto>(db, this.relatedObject5Id, SiteObjectDto);
}

function _foregroundImageMedium(this: ISiteObjectDto, db: IModelSource): Promise<IMediumDto> {
    return DBUtil.loadMediumId<IMediumDto>(db, this.foregroundImageMediumId, MediumDto);
}

function _backgroundImageMedium(this: ISiteObjectDto, db: IModelSource): Promise<IMediumDto> {
    return DBUtil.loadMediumId<IMediumDto>(db, this.backgroundImageMediumId, MediumDto);
}

function _menuImageMedium(this: ISiteObjectDto, db: IModelSource): Promise<IMediumDto> {
    return DBUtil.loadMediumId<IMediumDto>(db, this.menuImageMediumId, MediumDto);
}

function _style(this: ISiteObjectDto, db: IModelSource): Promise<IStyleDto> {
    return DBUtil.loadStyleId<IStyleDto>(db, this.styleId, StyleDto);
}

function _translations(this: ISiteObjectDto, db: IModelSource): Promise<ISiteObjectTranslationDto[]> {
    const siteObject = this;
    return new Promise( (resolve, reject) => {
        DBUtil.allSiteObjectTranslations<ISiteObjectTranslationDto>(db, SiteObjectTranslationDto)
            .then((translations) => {
                const match = translations.filter(x => x.siteObjectId === siteObject.id);
                if ( match.length > 0 ) {
                    resolve(match)
                } else {
                    reject('No translations found for siteObjectId:'+siteObject.id);
                }
            })
            .catch((error: Error) => reject(error));
    })
};

function _translation(this: ISiteObjectDto, db: IModelSource, language: ILanguageDto|number): Promise<ISiteObjectTranslationDto> {
    const languageId: Number = 
        ((language as ILanguageDto)?.id != undefined) ?
            Number((language as ILanguageDto).id ?? -1) :
            Number(language);

    const siteObject = this;
    return new Promise(function(resolve, reject) {
        siteObject.translations(db)
            .then(function(allTranslations) {
                const match = allTranslations.filter(x=>x.languageId === languageId);
                if ( match.length > 0 ) {
                    resolve(match[0]);
                } else {
                    reject("Unable to find matching translation in siteobject:" + siteObject.id + ", for language:" + languageId);
                }
            })
            .catch((error: Error) => reject(error));
    });
};

function _relatedObjects(this: ISiteObjectDto, db: IModelSource): Promise<Array<ISiteObjectDto>> {
    const siteObject = this;

    return new Promise(async function(resolve, reject) {
        const promises: Array<Promise<ISiteObjectDto>> = [];

        if ( siteObject.relatedObject1Id > 0 ) {
            promises.push(siteObject.relatedObject1(db));
        }

        if ( siteObject.relatedObject2Id > 0 ) {
            promises.push(siteObject.relatedObject2(db));
        }

        if ( siteObject.relatedObject3Id > 0 ) {
            promises.push(siteObject.relatedObject3(db));
        }

        if ( siteObject.relatedObject4Id > 0 ) {
            promises.push(siteObject.relatedObject4(db));
        }

        if ( siteObject.relatedObject5Id > 0 ) {
            promises.push(siteObject.relatedObject5(db));
        }

        let objects: Array<ISiteObjectDto> = [];

        if ( promises.length > 0 ) {
            objects = await Promise.all(promises);
        }

        resolve(objects);
    });
}

function SiteObjectDto(data: object) { 
    const obj = Transformers.create<ISiteObjectDto>(mappings, data); 
    
    /* addLinkables */
    obj.site = _site;
    obj.type = _type;
    obj.beacon = _beacon;
    obj.beaconSensitivity = _beaconSensitivity;
    obj.nextObject = _nextObject;
    obj.relatedObject1 = _relatedObject1;
    obj.relatedObject2 = _relatedObject2;
    obj.relatedObject3 = _relatedObject3;
    obj.relatedObject4 = _relatedObject4;
    obj.relatedObject5 = _relatedObject5;
    obj.foregroundImageMedium = _foregroundImageMedium;
    obj.backgroundImageMedium = _backgroundImageMedium;
    obj.menuImageMedium = _menuImageMedium;
    obj.style = _style;

    obj.translations = _translations;

    obj.translation = _translation;

    obj.relatedObjects = _relatedObjects;
        
    return obj;
}

export default SiteObjectDto
